import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import type { Run } from '../types';

export const SharedLinkJoin: React.FC = () => {
  const { shareCode } = useParams<{ shareCode: string }>();
  const navigate = useNavigate();
  const [run, setRun] = useState<Run | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRunByShareCode = async () => {
      if (!shareCode) {
        setError('Invalid share code');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const result = await api.safeCall(() =>
          api.getRunByShareCode(shareCode)
        );

        if (result.error) {
          setError(result.error);
          return;
        }

        if (result.data) {
          setRun(result.data);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load run');
      } finally {
        setLoading(false);
      }
    };

    fetchRunByShareCode();
  }, [shareCode]);

  const handleJoinRun = () => {
    if (run) {
      navigate(`/run/${run.id}`);
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading run details...</p>
        </div>
      </div>
    );
  }

  if (error || !run) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Run Not Found
          </h1>
          <p className="text-gray-600 mb-6">
            {error ||
              "The coffee run you're looking for doesn't exist or is no longer available."}
          </p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/')}
              className="w-full bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Browse Active Runs
            </button>
            <button
              onClick={() => navigate('/run/new')}
              className="w-full bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              Start Your Own Run
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md mx-auto px-4">
        <div className="bg-white shadow rounded-lg p-6">
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">☕</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Join Coffee Run
            </h1>
            <p className="text-gray-600">
              You've been invited to join this coffee run!
            </p>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {run.title}
                </h2>
                {run.description && (
                  <p className="text-gray-600">{run.description}</p>
                )}
              </div>

              <div className="grid grid-cols-1 gap-3 text-sm">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700">Created by:</span>
                  <span className="text-gray-900">{run.createdBy}</span>
                </div>

                <div className="flex justify-between">
                  <span className="font-medium text-gray-700">Status:</span>
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      run.status === 'Open'
                        ? 'bg-green-100 text-green-800'
                        : run.status === 'Collecting'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {run.status}
                  </span>
                </div>

                {run.pickupTime && (
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Pickup:</span>
                    <span className="text-gray-900 text-right">
                      {formatDateTime(run.pickupTime)}
                    </span>
                  </div>
                )}

                {run.pickupLocation && (
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Location:</span>
                    <span className="text-gray-900">{run.pickupLocation}</span>
                  </div>
                )}

                <div className="flex justify-between">
                  <span className="font-medium text-gray-700">
                    Current orders:
                  </span>
                  <span className="text-gray-900">
                    {run.orderCount} (${run.totalAmount.toFixed(2)})
                  </span>
                </div>
              </div>

              {run.status === 'Open' || run.status === 'Collecting' ? (
                <div className="pt-4">
                  <button
                    onClick={handleJoinRun}
                    className="w-full bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                  >
                    Join This Run & Add Your Order
                  </button>
                  <p className="text-xs text-gray-500 text-center mt-2">
                    You'll be able to add your order and see all the details
                  </p>
                </div>
              ) : (
                <div className="pt-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                    <p className="text-yellow-800 text-sm">
                      This run is no longer accepting new orders.
                    </p>
                  </div>
                  <button
                    onClick={handleJoinRun}
                    className="w-full mt-3 bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                  >
                    View Run Details
                  </button>
                </div>
              )}

              <div className="pt-4 border-t border-gray-200">
                <button
                  onClick={() => navigate('/')}
                  className="w-full text-indigo-600 px-4 py-2 text-sm hover:text-indigo-800 transition-colors"
                >
                  ← Back to All Runs
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Share Code:{' '}
            <span className="font-mono font-medium">{run.shareCode}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SharedLinkJoin;
